<template>
  <b-modal
    id="my-add-contact-popup"
    centered
    hide-footer
    hide-header
    body-class="my-add-contact-popup"
    size="lg"
    @show="beforeOpen"
    @hidden="isHide"
  >
    <button
      @click="hideModal"
      class="tw-absolute tw-top-[20px] tw-right-[20px] tw-grid tw-place-items-center tw-w-[40px] tw-h-[40px] tw-border-[1px] tw-border-transparent tw-text-[#d9d9d9] hover:tw-text-[var(--my-text-color)] tw-transition-colors"
    >
      <icon-close />
    </button>

    <form @submit.prevent="submitContact" novalidate>
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-y-[14px] md:tw-gap-x-[40px]">
        <div class="tw-flex tw-gap-[10px]">
          <div class="tw-flex-1">
            <my-form-input
              v-model="form.firstName"
              :label="$t('donor_dashboard.my_contacts_contact_form_first_name', 'First Name*')"
              :class="{ 'error-field': fieldErrors.firstName }"
              @blur="validateField('firstName')"
              required
              autocomplete="given-name"
            />
            <div
              v-if="fieldErrors.firstName"
              class="tw-text-red-500 tw-text-sm tw-mt-1"
              role="alert"
            >
              {{ fieldErrors.firstName }}
            </div>
          </div>
          <div class="tw-flex-1">
            <my-form-input
              v-model="form.lastName"
              :label="$t('donor_dashboard.my_contacts_contact_form_last_name', 'Last Name*')"
              :class="{ 'error-field': fieldErrors.lastName }"
              @blur="validateField('lastName')"
              required
              autocomplete="family-name"
            />
            <div
              v-if="fieldErrors.lastName"
              class="tw-text-red-500 tw-text-sm tw-mt-1"
              role="alert"
            >
              {{ fieldErrors.lastName }}
            </div>
          </div>
        </div>
        <div>
          <my-form-input
            v-model="form.email"
            :label="$t('donor_dashboard.my_contacts_contact_form_email_address', 'Email Address')"
            :class="{ 'error-field': fieldErrors.email }"
            @blur="validateField('email')"
            type="email"
            autocomplete="email"
          />
          <div v-if="fieldErrors.email" class="tw-text-red-500 tw-text-sm tw-mt-1" role="alert">
            {{ fieldErrors.email }}
          </div>
        </div>
        <div>
          <my-form-input
            v-model="form.address"
            :label="$t('donor_dashboard.my_contacts_contact_form_address', 'Address')"
            :class="{ 'error-field': fieldErrors.address }"
            @blur="validateField('address')"
            autocomplete="street-address"
          />
          <div v-if="fieldErrors.address" class="tw-text-red-500 tw-text-sm tw-mt-1" role="alert">
            {{ fieldErrors.address }}
          </div>
        </div>
        <div>
          <my-form-input
            v-model="form.expectedDonation"
            :prefix="currencySymbol"
            :label="
              $t(
                'donor_dashboard.my_contacts_contact_expected_donation_amount',
                'Expected Donation Amount'
              )
            "
            type="number"
            :class="{ 'error-field': fieldErrors.expectedDonation }"
            @blur="validateField('expectedDonation')"
            min="0"
            step="0.01"
            inputmode="decimal"
          />
          <div
            v-if="fieldErrors.expectedDonation"
            class="tw-text-red-500 tw-text-sm tw-mt-1"
            role="alert"
          >
            {{ fieldErrors.expectedDonation }}
          </div>
        </div>
        <div>
          <my-dropdown
            :value="form.relationship"
            variant="my-relationship-select"
            @set-relation="setRelation"
            :class="{ 'error-field': fieldErrors.relationship }"
          />
          <div
            v-if="fieldErrors.relationship"
            class="tw-text-red-500 tw-text-sm tw-mt-1"
            role="alert"
          >
            {{ fieldErrors.relationship }}
          </div>
        </div>
        <div>
          <my-phone-input
            v-model="form.phone"
            :label="$t('donor_dashboard.my_contacts_contact_phone', 'Phone*')"
            :class="{ 'error-field': fieldErrors.phone }"
            :default-country="defaultPhoneCountry"
            @blur="validateField('phone')"
            @validate="onPhoneValidate"
            required
            autocomplete="tel"
          />
          <div v-if="fieldErrors.phone" class="tw-text-red-500 tw-text-sm tw-mt-1" role="alert">
            {{ fieldErrors.phone }}
          </div>
        </div>
        <div v-if="isEdit">
          <div class="my-form-input">
            <label class="my-form-input__label">{{
              $t("donor_dashboard.my_contacts_move_contact", "Move contact to another team")
            }}</label>
            <multiselect-search
              v-model="teamModel"
              :multiselect-class="{ 'my-multiselect': true }"
              :open-prefetch="true"
              :search-url="getSearchUrl"
              :search-options="searchOptions"
              :disabled="loading"
            >
              <template #no-options>
                {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
              </template>
              <template #no-more-infinite>
                {{ $t("dashboard.team_search_no_more", "No more teams") }}
              </template>
            </multiselect-search>
          </div>
        </div>
      </div>

      <my-textarea v-model="form.notes" class="tw-mt-[48px] md:tw-mt-[42px]">
        <template v-slot:label>
          <span class="tw-flex tw-mb-[24px] tw-ps-[22px] tw-tracking-[0.01em]">
            {{ $t("donor_dashboard.my_contacts_notes_title", "Notes") }}
          </span>
        </template>
      </my-textarea>

      <div class="tw-text-center tw-mt-[44px] md:tw-mt-[40px]">
        <my-btn
          :loading="loading"
          btnStyle="my-primary"
          type="submit"
          :disabled="shouldPreventSubmit"
        >
          {{
            isEdit
              ? $t("donor_dashboard.my_contacts_btn_save_edit_contact", "Save Changes")
              : $t("donor_dashboard.my_contacts_btn_add_contact", "Add Contact")
          }}
        </my-btn>
        <div v-if="error" class="tw-text-red-500 tw-mt-2" role="alert">
          {{ error }}
        </div>
        <div v-if="success" class="tw-text-green-500 tw-mt-2" role="status">
          {{
            isEdit
              ? $t(
                  "donor_dashboard.my_contacts_contact_updated_success",
                  "Contact updated successfully!"
                )
              : $t(
                  "donor_dashboard.my_contacts_contact_added_success",
                  "Contact added successfully!"
                )
          }}
        </div>
      </div>
    </form>
  </b-modal>
</template>

<script>
import iconClose from "@/views/my-contacts/icons/close.vue";
import myFormInput from "@/views/my-contacts/ui/my-form-input.vue";
import myTextarea from "@/views/my-contacts/ui/my-textarea.vue";
import myBtn from "@/views/my-contacts/ui/my-btn.vue";
import myDropdown from "@/views/donor-dashboard/my-contacts/ui/my-dropdown.vue";
import myPhoneInput from "@/views/my-contacts/ui/my-phone-input.vue";
import { useTeamLeaderContacts } from "@/composables/useDashboardTeamLeaderContacts";
import { isValidPhoneNumber } from "libphonenumber-js";
import { mapState } from "vuex";
import MultiselectSearch from "@/components/multiselect-search.vue";
import urls from "@/constants/urls.js";
import axios from "axios";

export default {
  name: "add-contatc-popup",

  data() {
    return {
      currentVariant: "added",
      form: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        expectedDonation: "",
        expectedDonationCurrency: "",
        relationship: {},
        notes: "",
      },
      fieldErrors: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        expectedDonation: "",
        relationship: "",
      },
      contactId: null,
      loading: false,
      error: null,
      success: false,
      phoneValidationState: {
        isValid: false,
        twilioValid: null,
      },
      newTeam: null,
      selectedTeamOption: null,
    };
  },

  setup() {
    const teamLeaderContacts = useTeamLeaderContacts();

    return {
      teamLeaderContacts,
    };
  },

  computed: {
    ...mapState({
      userCountry: (state) => state.account.userCountry,
      campaignCurrency: (state) => state.donorMyContacts.campaignCurrency,
    }),

    getSearchUrl() {
      const orgId = localStorage.getItem("orgId");
      return urls.apiTeams
        .replace(":orgId", orgId)
        .replace(":campaignId", this.teamLeaderContacts.campaignId.value);
    },

    searchOptions() {
      return {
        placeholder: this.$t("donor_dashboard.select_team", "Select team"),
        changeParams: ({ query, page, per_page: pp }) => ({
          q: query,
          page: page,
          limit: pp,
        }),
        customLabel: (e) => `(${e.id}) ${e.name}`,
        processResults: (data) =>
          data.map((el) => ({
            id: el.id,
            ...el.attributes,
          })),
      };
    },

    teamModel: {
      get() {
        return this.selectedTeamOption;
      },
      set(option) {
        this.selectedTeamOption = option;
        this.newTeam = option ? option.id : null;
      },
    },

    isEdit() {
      return this.currentVariant === this.modalVariants.edit;
    },

    isAdded() {
      return this.currentVariant === this.modalVariants.added;
    },

    modalVariants() {
      return {
        added: "added",
        edit: "edit",
      };
    },

    currencySymbol() {
      try {
        // Use Intl.NumberFormat to get the currency symbol from the code
        const formatter = new Intl.NumberFormat("en", {
          style: "currency",
          currency: this.form.expectedDonationCurrency || this.campaignCurrency || "USD",
          currencyDisplay: "narrowSymbol",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        // Format 1 and extract the symbol by removing the number
        const parts = formatter.formatToParts(1);
        const symbolPart = parts.find((part) => part.type === "currency");
        return symbolPart ? symbolPart.value : "$";
      } catch (e) {
        return "$";
      }
    },

    hasFieldErrors() {
      return Object.values(this.fieldErrors).some((error) => error);
    },

    shouldPreventSubmit() {
      return this.hasFieldErrors;
    },

    defaultPhoneCountry() {
      console.debug("[AddContactPopup] Determining default phone country");
      console.debug("[AddContactPopup] User country check:", {
        userCountry: this.userCountry || "none",
      });

      // If we have a user country, use it as the default
      if (this.userCountry) {
        const countryCode = this.userCountry.toUpperCase();
        console.debug("[AddContactPopup] Using country from IP:", {
          countryCode,
          reason: "User country detected from IP",
        });
        return countryCode;
      }

      // Fallback to US if no user country detected
      console.debug("[AddContactPopup] Using fallback country:", {
        countryCode: "US",
        reason: "No user country detected, using default",
      });

      return "US";
    },
  },

  mounted() {
    this.$root.$on("my-add-contact-popup-data", this.setModalData);
  },

  beforeDestroy() {
    this.$root.$off("my-add-contact-popup-data", this.setModalData);
  },

  methods: {
    async hydrateSelectedTeam(teamId) {
      if (!teamId) {
        this.selectedTeamOption = null;
        return;
      }

      try {
        const { data } = await axios.get(this.getSearchUrl, {
          params: { team_id: teamId, limit: 1 },
        });
        const [team] = this.searchOptions.processResults(data.data);
        this.selectedTeamOption = team || null;
      } catch (e) {
        console.error(e);
        this.selectedTeamOption = null;
      }
    },

    setRelation(payload) {
      this.form.relationship = payload;
      this.validateField("relationship");
    },

    async setModalData(payload) {
      if (payload.edit && payload.data) {
        this.currentVariant = this.modalVariants.edit;
        this.teamLeaderContacts.teamId.value = payload.data.team_id;
        this.teamLeaderContacts.campaignId.value = payload.data.campaign_id;
        this.contactId = payload.data.id;

        this.form = {
          firstName: payload.data.first_name,
          lastName: payload.data.last_name,
          email: payload.data.email,
          phone: payload.data.phone,
          address: payload.data.address1,
          expectedDonation: payload.data.expected_donation_amount,
          expectedDonationCurrency: payload.data.expected_donation_currency,
          relationship: payload.data.relationship ? { [payload.data.relationship]: true } : {},
          notes: payload.data.note,
        };

        if (this.isEdit) {
          this.loading = true;
          await this.hydrateSelectedTeam(payload.data.team_id);
          this.loading = false;
        }
      }
    },

    beforeOpen() {
      // Reset form and errors on open
      this.form = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        expectedDonation: "",
        expectedDonationCurrency: this.campaignCurrency || "USD",
        relationship: {},
        notes: "",
      };
      this.fieldErrors = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        expectedDonation: "",
        relationship: "",
      };
      this.phoneValidationState = {
        isValid: false,
        twilioValid: null,
      };
      this.loading = false;
      this.error = null;
      this.success = false;
      this.newTeam = null;
      this.selectedTeamOption = null;
    },

    isHide() {
      this.currentVariant = this.modalVariants.added;
    },

    openModal() {
      this.$root.$emit("bv::show::modal", "my-add-contact-popup");
    },

    hideModal() {
      this.$root.$emit("bv::hide::modal", "my-add-contact-popup");
      this.teamLeaderContacts.teamId.value = null;
    },

    validateField(fieldName) {
      this.fieldErrors[fieldName] = "";

      switch (fieldName) {
        case "firstName":
          if (!this.form.firstName.trim()) {
            this.fieldErrors.firstName = "First name is required";
          } else if (this.form.firstName.length < 2) {
            this.fieldErrors.firstName = "First name must be at least 2 characters";
          }
          break;

        case "lastName":
          if (!this.form.lastName.trim()) {
            this.fieldErrors.lastName = "Last name is required";
          } else if (this.form.lastName.length < 2) {
            this.fieldErrors.lastName = "Last name must be at least 2 characters";
          }
          break;

        case "email":
          if (this.form.email && !this.isValidEmail(this.form.email)) {
            this.fieldErrors.email = "Please enter a valid email address";
          }
          break;

        case "address":
          break;

        case "phone":
          if (!this.form.phone.trim()) {
            this.fieldErrors.phone = "Phone is required";
          } else {
            try {
              // Stage 1: libphonenumber-js validates syntax/format
              // Stage 2: Twilio validates reachability (handled in onPhoneValidate)
              const isValid = isValidPhoneNumber(this.form.phone, this.defaultPhoneCountry);
              if (!isValid) {
                this.fieldErrors.phone = "Please enter a valid phone number";
              }
            } catch (error) {
              this.fieldErrors.phone = "Please enter a valid phone number";
            }
          }
          break;

        case "expectedDonation":
          if (this.form.expectedDonation && Number(this.form.expectedDonation) < 0) {
            this.fieldErrors.expectedDonation = "Amount must be a positive number";
          }
          break;
      }
    },

    validateAllFields() {
      this.validateField("firstName");
      this.validateField("lastName");
      this.validateField("email");
      this.validateField("address");
      this.validateField("phone");
      this.validateField("expectedDonation");

      return !this.hasFieldErrors;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    mapBackendErrors(errors) {
      // Reset field errors
      Object.keys(this.fieldErrors).forEach((key) => {
        this.fieldErrors[key] = "";
      });

      // Map backend field names to frontend field names
      const fieldMapping = {
        first_name: "firstName",
        last_name: "lastName",
        email: "email",
        phone: "phone",
        address1: "address",
        expected_donation_amount: "expectedDonation",
        relationship: "relationship",
      };

      if (errors && Array.isArray(errors)) {
        errors.forEach((error) => {
          const frontendField = fieldMapping[error.field];
          if (
            frontendField &&
            Object.prototype.hasOwnProperty.call(this.fieldErrors, frontendField)
          ) {
            this.fieldErrors[frontendField] = error.message;
          }
        });
      }
    },

    async submitContact() {
      // Clear previous errors
      this.error = null;
      this.success = false;

      // Validate all fields
      if (!this.validateAllFields()) {
        console.debug("[AddContactPopup] Field validation failed");
        return;
      }

      // Check Twilio validation if phone number is provided
      if (this.form.phone && !this.phoneValidationState.twilioValid) {
        console.debug("[AddContactPopup] Phone number validation failed");
        return;
      }

      this.loading = true;

      try {
        // Convert relationship object to string for backend
        let relationshipString = "";
        if (this.form.relationship && typeof this.form.relationship === "object") {
          relationshipString = Object.keys(this.form.relationship).find(
            (key) => this.form.relationship[key]
          );
        }

        if (this.isAdded) {
          await this.teamLeaderContacts.createContact({
            first_name: this.form.firstName,
            last_name: this.form.lastName,
            email: this.form.email,
            phone: this.form.phone,
            address1: this.form.address,
            expected_donation_amount: Number(this.form.expectedDonation) || 0,
            expected_donation_currency: this.campaignCurrency || "USD",
            team_id: Number(this.teamLeaderContacts.teamId.value),
            campaign_id: Number(this.teamLeaderContacts.campaignId.value),
            relationship: relationshipString,
            note: this.form.notes,
          });
          await this.teamLeaderContacts.fetchContacts({
            campaign_id: Number(this.teamLeaderContacts.campaignId.value),
            team_id: Number(this.teamLeaderContacts.teamId.value),
          });
        } else if (this.isEdit) {
          const updatedContact = await this.teamLeaderContacts.updateContact(this.contactId, {
            first_name: this.form.firstName,
            last_name: this.form.lastName,
            email: this.form.email,
            phone: this.form.phone,
            address1: this.form.address,
            expected_donation_amount: Number(this.form.expectedDonation) || 0,
            expected_donation_currency: this.campaignCurrency || "USD",
            team_id: Number(this.newTeam || this.teamLeaderContacts.teamId.value),
            campaign_id: Number(this.teamLeaderContacts.campaignId.value),
            relationship: relationshipString,
            note: this.form.notes,
          });
          await this.teamLeaderContacts.fetchContactById(updatedContact.id);
          this.teamLeaderContacts.teamId.value = null;
          this.teamLeaderContacts.fetchContacts({
            campaign_id: Number(this.teamLeaderContacts.campaignId.value),
          });
        }

        this.success = true;
        setTimeout(() => {
          this.hideModal();
        }, 1500);
      } catch (err) {
        // Handle API errors
        if (err.errors && Array.isArray(err.errors)) {
          this.mapBackendErrors(err.errors);

          // If there are field-specific errors, show a general message
          if (this.hasFieldErrors) {
            this.error = this.$t(
              "donor_dashboard.my_contacts_error_correct_in_form",
              "Please correct the errors in the form"
            );
          } else {
            // Show general error if no field-specific errors
            const errorMessages = err.errors.map((error) => error.message).join("; ");
            this.error =
              errorMessages ||
              err.message ||
              this.$t("donor_dashboard.my_contacts_error_failed_save", "Failed to save contact.");
          }
        } else {
          this.error =
            err.message ||
            this.$t(
              "donor_dashboard.my_contacts_error_failed_save_try_again",
              "Failed to save contact. Please try again."
            );
        }
      } finally {
        this.loading = false;
      }
    },

    onPhoneValidate(validationResult) {
      // Update validation state - now tracking both stages
      this.phoneValidationState.isValid = validationResult.isValidFormat; // Stage 1: libphonenumber syntax
      this.phoneValidationState.twilioValid = validationResult.twilioValidation?.isValid; // Stage 2: Twilio reachability

      // Clear any existing phone validation error when format is valid
      if (validationResult.isValidFormat) {
        // Clear format-related errors
        if (this.fieldErrors.phone === "Please enter a valid phone number") {
          this.fieldErrors.phone = "";
        }
      }

      // Handle Twilio validation (Stage 2) results
      if (validationResult.twilioValidation && validationResult.twilioValidation.hasError) {
        this.fieldErrors.phone = "Phone number could not be verified";
      } else if (validationResult.twilioValidation && validationResult.twilioValidation.isValid) {
        // Clear Twilio-related errors when verified
        if (this.fieldErrors.phone === "Phone number could not be verified") {
          this.fieldErrors.phone = "";
        }
      }

      this.validateField("phone");
    },
  },

  components: {
    iconClose,
    myFormInput,
    myTextarea,
    myBtn,
    myDropdown,
    myPhoneInput,
    MultiselectSearch,
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-dialog .modal-content {
  @apply md:tw-rounded-[20px];
}

::v-deep .modal-dialog .my-add-contact-popup {
  @apply tw-pt-[60px] tw-px-[44px] tw-pb-[84px] md:tw-pb-[40px] md:tw-rounded-[20px] tw-font-lato tw-text-[var(--my-text-color)] tw-leading-[var(--my-leading)] tw-bg-[var(--my-white)];
}

::v-deep .error-field {
  border-bottom-color: #ef4444 !important;
}

::v-deep .error-field input {
  color: #ef4444 !important;
}

::v-deep .error-field .my-form-input__label {
  color: #ef4444 !important;
}

.my-form-input {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 3px;

  &__label {
    margin-bottom: 0;
  }

  &__container {
    display: flex;
    gap: 1em;
    align-items: center;
  }
}

::v-deep .multiselect.my-multiselect {
  @apply tw-w-full md:tw-w-[276px] tw-rounded-full;

  .multiselect__tags {
    @apply tw-flex tw-items-center tw-min-h-[55px] md:tw-min-h-[55px] tw-ps-[24px] tw-pe-[48px] md:tw-ps-[44px] md:tw-px-[52px] tw-rounded-full tw-border-[var(--my-white-soft)] tw-bg-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__select {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%];

    &:before {
      @apply tw-border-t-[var(--my-primary)];
    }
  }

  &.multiselect--active .multiselect__select {
    @apply tw-top-[50%];
    transform: rotateZ(180deg) translateY(50%);
  }

  .multiselect__content-wrapper {
    @apply tw-rounded-[20px];
  }

  .multiselect__single,
  .multiselect__input,
  .multiselect__placeholder {
    @apply tw-text-[18px] tw-text-[var(--my-text-color)] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-bg-[var(--my-white-soft)] tw-border-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__element .multiselect__option {
    @apply tw-block tw-truncate;
  }

  .multiselect__option--highlight {
    @apply tw-bg-[var(--my-primary)];
  }

  .multiselect__spinner {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%] tw-bg-[var(--my-white-soft)];
  }

  &.multiselect--disabled {
    .multiselect__select {
      @apply tw-bg-[var(--my-white-soft)];
    }
  }
}
</style>
